/* eslint-disable react/no-danger */
import React, { useCallback, useEffect, useMemo, useState, Fragment, useContext } from 'react';
import { Col, useDeviceType } from '@cian/ui-kit';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import {
  buildPostUrl,
  prepareListingOrdering,
  preparePageNumber,
  useGetListing,
  useTimeoutEffect,
  prepareUserAvatar,
  prepareQSParam,
  unescapeHtml,
  canDeleteAllImagesFromPosts,
  ApplicationContext,
} from '../../utils';
import { getQuestionList, resetQuestionList } from '../../actions/questionList';
import { QUESTION_LIST_REQUEST_LIMIT, CIAN_URL } from '../../constants';
import { IApplicationState, TThunkDispatch } from '../../types/redux';
import { selectListingAppendType } from '../../selectors/settings/selectListingAppendType';
import { selectCurrentPageMeta, selectPageMetaItems } from '../../selectors/pagesMeta';
import { ListingHeaderContainer } from '../ListingHeader';
import {
  CardListingItem,
  ListingItem,
  SkeletonCardListingItem,
  SkeletonListingItem,
} from '../../components/ListingItem';
import { ERequestStatus } from '../../types/requestStatus';
import { EType as EPostType } from '../../repositories/journal/entities/journal/JournalListAttributesSchema';
import { PaginationContainer } from '../Pagination';
import { selectQuestionList } from '../../selectors/questionList';
import { EListingTypes } from '../../types/listing';
import { selectFavoriteListingType } from '../../selectors/settings/selectFavoriteListingType';
import { EType } from '../../repositories/journal/entities/page_info/PageInfoSchema';
import { UserCard } from '../../components/UserCard';
import { BreadCrumbsContainer } from '../BreadCrumbs';
import { NothingFound } from '../../components/NotFound';
import { FindRealtorBannerAndModalContainer } from '../FindRealtorBannerAndModal';
import { prepareSubtitle } from './helpers';
import { QuestionsSubscribeFormContainer } from '../QuestionsSubscribeForm';
import * as s from './Listings.css';

/**
 * Листинг вопросов
 */
export const QuestionList = () => {
  const dispatch = useDispatch<TThunkDispatch>();
  const history = useHistory();
  const { search, pathname } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const qsPage = preparePageNumber(searchParams.get('page') as string);
  const qsOrdering = prepareListingOrdering(searchParams.get('ordering') || '') || '';
  const qsAuthorId = prepareQSParam(searchParams.get('author_id'));
  const qsCommentUserId = prepareQSParam(searchParams.get('comment_user_id'));
  const appendType = useSelector(selectListingAppendType);
  const favoriteListingType = useSelector(selectFavoriteListingType);
  const {
    type: pageType,
    pathname: currentPageTypePathname,
    category,
  } = useSelector((state: IApplicationState) => selectCurrentPageMeta(state, pathname));
  const pagesMeta = useSelector(selectPageMetaItems);
  const deviceType = useDeviceType();
  const [isShownAsList, setIsShownAsList] = useState(false);
  const isPhone = () => deviceType === 'phone';
  const isDesktop = () => deviceType === 'desktop';
  const { items: questions, status: questionsStatus } = useSelector(selectQuestionList);
  const [isSkeletonShown, setIsSkeletonShown] = useState(
    questionsStatus === ERequestStatus.Loading && appendType === 'set',
  );
  // Только на десктопе в режиме карточек форма подписки стоит после 6-ого элемента
  const subscribeFormPositionIndex = isDesktop() && !isShownAsList ? 6 : 4;
  const {
    custom: { deleteAllImagesFromPostsSettings },
  } = useContext(ApplicationContext);

  /** Если не нашел нужный тип, выкидывает на дефолтный */
  useEffect(() => {
    if (EType.Questions !== pageType) {
      history.replace(currentPageTypePathname);
    }
  }, [currentPageTypePathname, history, pageType]);

  /** Запрашивает листинг/сохраняет в стор/трекает показ листинга */
  useGetListing(
    () => {
      dispatch(
        getQuestionList({
          tag: undefined,
          offset: (qsPage - 1) * QUESTION_LIST_REQUEST_LIMIT,
          limit: QUESTION_LIST_REQUEST_LIMIT,
          setType: appendType,
          ordering: qsOrdering || undefined,
          category: category || undefined,
          authorId: parseInt(qsAuthorId, 10) || undefined,
          commentUserId: parseInt(qsCommentUserId, 10) || undefined,
        }),
      ).finally();
    },
    { dependencyNames: ['page', 'ordering', 'category', 'deviceType', 'authorId', 'commentUserId'] },
  );

  /** При выходе сбрасывает состояние вопросов */
  useEffect(() => {
    return () => {
      dispatch(resetQuestionList());
    };
  }, [dispatch]);

  /** Устанавливает вид листинга: карточки или список */
  useEffect(() => {
    setIsShownAsList(favoriteListingType === EListingTypes.List && deviceType !== 'phone');
  }, [favoriteListingType, deviceType]);

  /** Создает небольшую задержку перед скрытием скелетона, когда элементов не найдено */
  useTimeoutEffect(() => {
    setIsSkeletonShown(questionsStatus === ERequestStatus.Loading && appendType === 'set');
  }, [!isSkeletonShown || questions.length ? 0 : 500]);

  /**
   * При клике по карточке вопроса роутит на соответствующую страницу вопроса
   * При клике по шильдику категории в карточке роутит на соответствующую категорию
   */
  const handleClick = useCallback(
    (e: React.MouseEvent, url: string) => {
      e.preventDefault();
      history.push(url);
    },
    [history],
  );

  /**
   * При клике по карточке пользователя отправляет на соответствующую страницу профиля
   */
  const handleUserCardClick = (e: React.MouseEvent, url: string) => {
    e.preventDefault();
    window.location.href = url;
  };

  return (
    <>
      <ListingHeaderContainer />
      <FindRealtorBannerAndModalContainer />

      <div className={isShownAsList ? s['list-wrapper'] : s['cards-wrapper']}>
        {isSkeletonShown &&
          [...Array(6)].map((_, index) =>
            isShownAsList ? (
              <div key={index} className={s['list-item-wrapper']}>
                <SkeletonListingItem />
              </div>
            ) : (
              <Col key={index} xs={12} m={6} l={4}>
                <SkeletonCardListingItem />
              </Col>
            ),
          )}

        {((questionsStatus === ERequestStatus.Succeed && appendType === 'set') || appendType === 'append') &&
          questions.map(
            (
              {
                id,
                attributes: {
                  image,
                  title,
                  subtitle,
                  datePublish,
                  commentsCount,
                  category,
                  slug,
                  authorObject,
                  noIndex,
                  type,
                  likesCount,
                },
              },
              index,
            ) => {
              const pageMeta = pagesMeta.find(
                pageMeta => pageMeta.type === EType.Questions && pageMeta.category === category,
              );

              return (
                <Fragment key={`blog-list-item-${index}`}>
                  {/* Форма подписки */}
                  {index === subscribeFormPositionIndex && (
                    <div className={`${s['insert-wrapper']} ${s['_active']}`}>
                      <QuestionsSubscribeFormContainer />
                    </div>
                  )}

                  {isShownAsList ? (
                    <div key={index} className={s['list-item-wrapper']}>
                      {/* Элемент списка */}
                      <ListingItem
                        title={unescapeHtml(title)}
                        subtitle={prepareSubtitle(subtitle, type)}
                        image={
                          canDeleteAllImagesFromPosts(new Date(datePublish), deleteAllImagesFromPostsSettings)
                            ? undefined
                            : image
                        }
                        rubric={pageMeta ? { name: pageMeta.title, url: pageMeta.pathname } : undefined}
                        datePublish={datePublish}
                        numComments={commentsCount}
                        numLikes={likesCount}
                        url={buildPostUrl({ type: EPostType.Questions, slug, id })}
                        noIndex={Boolean(noIndex)}
                        type={type}
                        onRubricButtonClick={handleClick}
                        onClick={e => handleClick(e, buildPostUrl({ type: EPostType.Questions, slug, id }))}
                      >
                        {authorObject && authorObject?.userId && (
                          <UserCard
                            imageSrc={prepareUserAvatar(authorObject?.avatar || undefined)}
                            name={
                              authorObject.fullName ||
                              authorObject?.companyName ||
                              (authorObject?.userId ? `ID: ${authorObject.userId}` : 'Аноним')
                            }
                            description={(authorObject.fullName && authorObject?.companyName) || undefined}
                            isTrusted={authorObject?.userTrust || undefined}
                            url={authorObject?.specialistLink ? `${CIAN_URL}${authorObject.specialistLink}` : undefined}
                            onClick={e =>
                              authorObject?.specialistLink &&
                              handleUserCardClick(e, `${CIAN_URL}${authorObject.specialistLink}`)
                            }
                          />
                        )}
                      </ListingItem>
                    </div>
                  ) : (
                    <Col key={index} xs={12} m={6} l={4}>
                      <CardListingItem
                        title={unescapeHtml(title)}
                        subtitle={prepareSubtitle(subtitle, type)}
                        height={isPhone() ? undefined : 400}
                        rubric={pageMeta ? { name: pageMeta?.title, url: pageMeta?.pathname } : undefined}
                        datePublish={datePublish}
                        numComments={commentsCount}
                        numLikes={likesCount}
                        url={buildPostUrl({ type: EPostType.Questions, slug, id })}
                        noIndex={Boolean(noIndex)}
                        type={type}
                        onRubricButtonClick={handleClick}
                        onClick={e => handleClick(e, buildPostUrl({ type: EPostType.Questions, slug, id }))}
                      >
                        {authorObject && authorObject?.userId && (
                          <UserCard
                            imageSrc={prepareUserAvatar(authorObject?.avatar || undefined)}
                            name={
                              authorObject.fullName ||
                              authorObject?.companyName ||
                              (authorObject?.userId ? `ID: ${authorObject.userId}` : 'Аноним')
                            }
                            description={(authorObject.fullName && authorObject?.companyName) || undefined}
                            isTrusted={authorObject?.userTrust || undefined}
                            url={authorObject?.specialistLink ? `${CIAN_URL}${authorObject.specialistLink}` : undefined}
                            onClick={e =>
                              authorObject?.specialistLink &&
                              handleUserCardClick(e, `${CIAN_URL}${authorObject.specialistLink}`)
                            }
                          />
                        )}
                      </CardListingItem>
                    </Col>
                  )}
                </Fragment>
              );
            },
          )}

        {[ERequestStatus.Succeed, ERequestStatus.Failed].includes(questionsStatus) && !questions.length && (
          <NothingFound pathname={currentPageTypePathname} showResetFiltersLink={qsPage > 1} />
        )}
      </div>

      {questionsStatus === ERequestStatus.Succeed && (
        <div className={s['controls-wrapper']}>
          <PaginationContainer />
        </div>
      )}

      {/* Хлебные крошки для мобилки */}
      {isPhone() && (
        <div className={s['bread-crumbs-wrapper']}>
          <BreadCrumbsContainer />
        </div>
      )}
    </>
  );
};
